import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { PLAN_PRICES } from '@/config/pricing';
import {
  SAMPLE_REPORT_PUBLISHED, SAMPLE_REPORT_LINK_TEXT,
  SUBJECT, SCORE, DIMENSION_SCORES, FINDINGS,
} from '@/pages/sampleReportContent';
import { AccordionItem } from '@/components/ds/AccordionItem';
import '@/styles/forms.css';
import '@/styles/landing.css';
// .uxa-visually-hidden lives in data.css and .uxa-inline-link in
// sample-report.css. Imported rather than redeclared: a second copy of a
// link role is how /sample-report/ ended up owning radii it should not have.
import '@/styles/data.css';
import '@/styles/sample-report.css';

/**
 * /compare — UXAuditPro against Hotjar and UserTesting.
 *
 * ── A RE-CUT, NOT NEW WRITING ───────────────────────────────────────────────
 *
 * Every argument here already exists in
 * /blog/uxauditpro-vs-hotjar-vs-usertesting/. This page is the decision, that
 * page is the reasoning: a buyer comparing tools wants the shape in thirty
 * seconds, and a reader researching the category wants the 2,000 words. Both
 * are legitimate and they are different intents.
 *
 * WHICH IS ALSO THE DUPLICATE-CONTENT ANSWER. Two URLs arguing the same case is
 * a real risk, and the mitigation is that this one is SHORTER and framed round
 * the choice rather than the analysis, and it links to the article as the long
 * version rather than competing with it. Nothing is copied verbatim at length.
 *
 * ── THE CORRECTION THIS PAGE WOULD NOT CARRY ────────────────────────────────
 *
 * The source article says the product "analyses your product across seven UX
 * dimensions, navigation, visual hierarchy, accessibility, conversion, mobile
 * UX, content clarity, and performance". THAT IS FALSE. There are SIX, they are
 * fixed, and two of the named ones are not among them:
 *
 *     Navigation, Visual Hierarchy, Friction Points,
 *     Accessibility, Conversion Design, Mobile UX
 *
 * "Content clarity" and "performance" are not dimensions, and Friction Points
 * is missing from the list. The claim appears EIGHT times across blogContent.js
 * and BlogIndexPage.js. It is flagged to the owner and logged rather than
 * silently rewritten — CLAUDE.md permits removing a demonstrably false claim
 * only when each one is raised individually first.
 *
 * What is not negotiable is that this page does not repeat it. Re-cutting
 * content is not a licence to re-publish its errors, least of all on a page
 * whose entire job is credibility.
 *
 * ── SOURCING ────────────────────────────────────────────────────────────────
 *
 * | Claim                    | Source                                        |
 * |--------------------------|-----------------------------------------------|
 * | six dimensions, by name  | server._DIM_TO_FIELD, test_six_dimensions.py  |
 * | one free audit a month   | pricing.PLANS["FREE"]                         |
 * | one free audit a month   | PLAN_PRICES.FREE.quota / monthly_audits = 1   |
 * | per-audit price          | PLAN_PRICES.STARTER.usd                       |
 * | up to 3 pages            | page_set.MAX_PAGES_PAID                       |
 *
 * Nothing is claimed about Hotjar's or UserTesting's pricing beyond its SHAPE
 * (free tier / quote-based), because their numbers change and a stale figure
 * about a competitor is both wrong and unfair. That restraint is carried over
 * from the article, which makes the same point explicitly.
 */

/* The comparison table's rows. Data, so the table markup stays one loop and a
   new row cannot arrive with its own styling. */
const ROWS = [
  {
    label: 'Answers',
    ours: 'What is wrong, and what to fix first',
    hotjar: 'What users do',
    ut: 'Why users do it',
  },
  {
    label: 'Time to insight',
    ours: 'Minutes',
    hotjar: 'Days to weeks, once traffic builds',
    ut: 'Days',
  },
  {
    label: 'Needs live traffic',
    ours: 'No',
    hotjar: 'Yes',
    ut: 'No',
  },
  {
    label: 'Gives a score',
    ours: 'Yes, across six dimensions',
    hotjar: 'No',
    ut: 'No',
  },
  {
    label: 'Setup',
    ours: 'A URL. Nothing to install',
    hotjar: 'A tracking script',
    ut: 'Panel recruitment',
  },
  {
    label: 'Accessibility',
    ours: 'Scored as one of the six dimensions',
    hotjar: 'Not covered',
    ut: 'Only if you script for it',
  },
  {
    label: 'Audit a competitor',
    ours: 'Yes, any public URL',
    hotjar: 'No, the script has to be on the page',
    ut: 'Possible, at panel cost',
  },
  {
    label: 'What you hand engineers',
    ours: 'A PDF report and a ranked fix list',
    hotjar: 'Heatmaps and session clips',
    ut: 'Video highlights',
  },
  {
    label: 'Ongoing monitoring',
    ours: 'No, point-in-time \u2014 re-run when you ship',
    hotjar: 'Yes, continuous',
    ut: 'No, per study',
  },
  {
    label: 'Pricing shape',
    ours: 'One free audit a month, then per audit',
    hotjar: 'Free tier, paid scales with traffic',
    ut: 'Quote-based',
  },
];

const TOOLS = [
  {
    name: 'UXAuditPro',
    role: 'A diagnosis',
    body: 'Give it a URL and it scores the page across six dimensions — '
      + 'Navigation, Visual Hierarchy, Friction Points, Accessibility, '
      + 'Conversion Design and Mobile UX — then returns every finding with the '
      + 'evidence behind it and what to change.',
    wins: 'Speed and structure, with no traffic required. It runs on any public '
      + 'URL, so you can point it at a competitor.',
    limits: 'A point-in-time audit, not ongoing monitoring. It does not watch '
      + 'real sessions and it does not interview anybody.',
  },
  {
    name: 'Hotjar',
    role: 'A microscope',
    body: 'Install a tracking script and it records what users actually do: '
      + 'heatmaps, scroll depth, and recordings of individual sessions.',
    wins: 'Ground truth. When you already suspect a flow is broken, a handful '
      + 'of recordings confirms or kills the theory fast.',
    limits: 'It shows the what, not the why, and surfaces nothing on its own — '
      + 'you have to know what to look for. It needs traffic, so it is no help '
      + 'before launch.',
  },
  {
    name: 'UserTesting',
    role: 'A conversation',
    body: 'Define a task, recruit participants from their panel, and get back '
      + 'videos of real people attempting it, often thinking aloud.',
    wins: 'Depth. There is no substitute for watching someone misunderstand '
      + 'your product in a way you never anticipated.',
    limits: 'Speed and cost. A study takes days, and it gives no structured '
      + 'overview of overall health. You bring a specific question.',
  },
];

/* THE SHOWCASED FINDING IS READ, NOT WRITTEN.
 *
 * The handoff fills this card with "TechFlow Dashboard · #TF-2024", a
 * Conversion Design finding about a checkout CTA, "one of eighteen", and the
 * scores 54 and 72. None of it exists. There is no TechFlow audit, the real
 * record carries EIGHT verified findings and scores 50 overall, and this site
 * has had invented specifics stripped out of it before.
 *
 * So the layout is the handoff's and every value is the real record's:
 * sampleReportContent.js, transcribed from audit_1512ecaff8a6 (roarsinc.com,
 * 2026-08-31) — the same fixture /sample-report/ and the cross-page section
 * read. Pinned by test_compare_finding_matches_fixture.py.
 *
 * The handoff's four quadrants are ELEMENT / EVIDENCE / FIX / RANKED. The
 * record has no element/evidence split — a finding carries `title`,
 * `userImpact`, `recommendation`, `effort` and `impact` — so the quadrants are
 * the four fields that actually exist. Inventing an "evidence" line to fill
 * the fourth box is the exact defect this comment is about.
 */
const SHOWCASE = FINDINGS.find((f) => f.severity === 'critical');

/* Answers to the five questions, all of them restatements of claims this page
   or the source article already makes. No FAQPage JSON-LD — see the note in
   the structuredData block below, which predates this section and still
   applies: the article carries the markup for these questions and a second URL
   emitting the same Q&A competes with itself. */
const FAQS = [
  {
    q: 'Do I need traffic to run an audit?',
    a: 'No. It runs on any public URL, so it works the day before launch and '
      + 'on a page nobody has visited yet. Behavioural tools cannot say '
      + 'anything until traffic builds.',
  },
  {
    q: 'Can I audit a competitor?',
    // The handoff's answer ended "on the same six dimensions", which
    // no-fabricated-claims bans as [scope-same-six]: mobileUX is dropped when
    // a run has no mobile evidence, so not every audit scores six. Reworded to
    // the claim the product can honour -- both pages are scored the same way,
    // which is the point the answer is actually making.
    a: 'Yes. A URL is the only input, so you can score their checkout against '
      + 'yours on exactly the same dimensions. A tracking script cannot be '
      + "installed on somebody else's site.",
  },
  {
    q: 'Does an audit replace user research?',
    a: 'No, and it is not meant to. It tells you where to point research, '
      + 'which is the difference between a study that answers something and a '
      + 'study that confirms what a heuristic pass would have caught for free.',
  },
  {
    q: 'What do I actually get back?',
    a: 'A score across six dimensions, desktop and mobile screenshots, every '
      + 'finding with the element and the evidence behind it, and a fix list '
      + 'ranked by impact and effort. Exportable as a PDF for the sprint review.',
  },
];

const STAGES = [
  {
    stage: 'Stage 01',
    title: 'Pre-launch',
    body: 'No traffic, no panel budget, and a deadline. A heatmap has nothing '
      + 'to measure and a study takes weeks you do not have.',
    verdict: 'Run an audit. Nothing else earns its cost yet.',
  },
  {
    stage: 'Stage 02',
    title: 'First year, real traffic',
    body: 'Enough sessions for behavioural data to mean something, but no '
      + 'research budget and no time to spend it on the obvious.',
    verdict: 'Audit for the map, then watch the flows it flags.',
  },
  {
    stage: 'Stage 03',
    title: 'Established',
    body: 'All three tools are affordable. The risk is no longer cost, it is '
      + 'pointing an expensive study at a question a five-minute audit '
      + 'already answered.',
    verdict: 'Audit to aim. Research what is left.',
  },
];

export default function ComparePage() {
  const { login, user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="uxa-landing">
      <SEO
        title="UXAuditPro vs Hotjar vs UserTesting"
        description={
          'Three tools, three different jobs. A scored UX audit in minutes, '
          + 'behavioural analytics, or moderated research — which one answers '
          + 'the question you actually have.'
        }
        canonical="/compare"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'UXAuditPro vs Hotjar vs UserTesting',
          url: 'https://www.uxauditpro.com/compare/',
          description:
            'A side-by-side comparison of UXAuditPro, Hotjar and UserTesting '
            + 'by the question each one answers.',
          // NO FAQPage here. The source article already carries FAQPage markup
          // for these same questions; a second URL emitting the same Q&A is
          // duplicate structured data competing with itself.
          about: [
            { '@type': 'SoftwareApplication', name: 'UXAuditPro' },
            { '@type': 'SoftwareApplication', name: 'Hotjar' },
            { '@type': 'SoftwareApplication', name: 'UserTesting' },
          ],
        }}
      />

      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="uxa-hero-eyebrow">Three tools &middot; Three jobs</span>
        <h1 className="uxa-h1" style={{ margin: 'var(--space-6) auto 0' }}>
          UXAuditPro vs Hotjar vs UserTesting
        </h1>
        <p className="uxa-lead" style={{ maxWidth: 660, margin: 'var(--space-5) auto 0', color: 'var(--ink-2)' }}>
          Three tools, three genuinely different jobs. Most &ldquo;which is
          best&rdquo; arguments are two people comparing tools built to answer
          different questions. Work out which question you have and the choice
          stops being a debate.
        </p>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: '0 0 var(--space-12)' }}>
          The short version
        </h2>
        {/* STILL A REAL <table>, inside the handoff's card.
            The design draws this as a grid of divs. A comparison is exactly
            the content a table element exists for: <th scope="col"> on each
            tool and <th scope="row"> on each capability is what makes a screen
            reader say "Audit a competitor, Hotjar, No, the script has to be on
            the page" instead of reading forty unlabelled cells in a row. The
            card is the presentation; the table is the structure.

            overflow-x lives on the card, not the table: ten rows across four
            columns is wider than a phone, and the page body must never scroll
            sideways. */}
        <div className="uxa-cmp-matrix-card">
          <table className="uxa-cmp-matrix">
            <thead>
              <tr>
                <th scope="col" className="uxa-cmp-matrix__lead">Capability</th>
                <th scope="col" className="uxa-cmp-matrix__ours">
                  <span className="uxa-cmp-mark--ours" aria-hidden="true">UX</span>
                  UXAuditPro
                </th>
                <th scope="col">
                  <span className="uxa-cmp-mark uxa-cmp-mark--ring" aria-hidden="true" />
                  Hotjar
                </th>
                <th scope="col">
                  <span className="uxa-cmp-mark uxa-cmp-mark--diamond" aria-hidden="true" />
                  UserTesting
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label}>
                  <th scope="row">{r.label}</th>
                  <td className="uxa-cmp-matrix__ours">{r.ours}</td>
                  <td>{r.hotjar}</td>
                  <td>{r.ut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: '0 0 var(--space-12)' }}>
          What each one is for
        </h2>
        {/* .uxa-price-card, NOT .uxa-surface-float. The float surface has a
            CLOSED consumer list, and surfaces-radius-glow caught this card
            joining it: its three members are report surfaces floating over the
            spectrum glow, and a marketing comparison card is not one. Widening
            that list to fit this page would have eroded a closed rule for
            convenience -- the guard was right and the page was wrong.
            .uxa-price-card is the bordered marketing panel this actually is,
            and it already carries the isolation the primary CTA's bloom needs.

            The comment lived INSIDE the map's arrow body on the first attempt,
            which makes two JSX siblings out of a single-element return. Jest
            passed -- every guard here reads source rather than compiling it --
            and `npm run build` caught it. A source guard cannot tell you the
            file parses. */}
        <div className="uxa-compare-grid">
          {TOOLS.map((t) => (
            <article key={t.name} className="uxa-price-card uxa-compare-card">
              <h3 className="uxa-h3" style={{ margin: 0 }}>{t.name}</h3>
              <p className="uxa-dense uxa-compare-role">{t.role}</p>
              <p className="uxa-body">{t.body}</p>
              <p className="uxa-dense"><strong>Where it wins.</strong> {t.wins}</p>
              <p className="uxa-dense"><strong>Where it falls short.</strong> {t.limits}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: 0 }}>
          What a finding actually looks like
        </h2>
        <p
          className="uxa-lead"
          style={{ color: 'var(--ink-2)', margin: 'var(--space-4) auto 0', maxWidth: 620, textAlign: 'center' }}
        >
          {`Every finding names what is wrong, who it hurts and what to change, `
            + `then ranks the fix by impact and effort. This is one of `
            + `${FINDINGS.length} from a real audit.`}
        </p>

        <div
          className="uxa-cmp-float"
          style={{ maxWidth: 900, margin: 'var(--space-8) auto 0', padding: 'var(--space-8)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span
                className="uxa-cmp-label"
                style={{
                  padding: '5px 12px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--severity-major-fill)',
                  color: 'var(--severity-major-text)',
                  fontWeight: 700,
                }}
              >
                {SHOWCASE.severity}
              </span>
              <span className="uxa-dense" style={{ color: 'var(--ink-3)', margin: 0 }}>
                {SHOWCASE.dimensionName}
              </span>
            </span>
            <span className="uxa-dense" style={{ color: 'var(--ink-3)', margin: 0 }}>
              {`${SUBJECT.appName} \u00b7 ${SUBJECT.auditId}`}
            </span>
          </div>

          <h3 className="uxa-h3" style={{ margin: 'var(--space-4) 0 0' }}>{SHOWCASE.title}</h3>

          <div className="uxa-cmp-finding-grid">
            <div>
              <div className="uxa-cmp-label">Who it hurts</div>
              <p className="uxa-dense" style={{ margin: 'var(--space-1) 0 0' }}>{SHOWCASE.userImpact}</p>
            </div>
            <div>
              <div className="uxa-cmp-label">Fix</div>
              <p className="uxa-dense" style={{ margin: 'var(--space-1) 0 0' }}>{SHOWCASE.recommendation}</p>
            </div>
            <div>
              <div className="uxa-cmp-label">Ranked</div>
              <p className="uxa-dense" style={{ margin: 'var(--space-1) 0 0', fontWeight: 500 }}>
                {`${SHOWCASE.effort} effort \u00b7 ${SHOWCASE.impact} impact`}
              </p>
            </div>
            <div>
              <div className="uxa-cmp-label">Audited</div>
              <p className="uxa-dense" style={{ margin: 'var(--space-1) 0 0' }}>
                {`${SUBJECT.url} \u00b7 ${SUBJECT.completedAt}`}
              </p>
            </div>
          </div>

          {/* Two meters, ONE fill colour. The handoff draws the second bar in
              #6F7BFF; there is no blue in this palette and adding one here
              would make the page the only surface on the site running two
              accents. */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-6)', marginTop: 'var(--space-5)', flexWrap: 'wrap' }}>
            {[
              { label: SHOWCASE.dimensionName, value: DIMENSION_SCORES[SHOWCASE.dimension] },
              { label: 'Overall score', value: SCORE.value },
            ].map((m) => (
              <div key={m.label} style={{ flex: '1 1 200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="uxa-dense" style={{ margin: 0, color: 'var(--ink-2)', fontWeight: 500 }}>{m.label}</span>
                  <span className="uxa-dense" style={{ margin: 0, color: 'var(--ink-1)', fontWeight: 700 }}>{m.value}</span>
                </div>
                <div className="uxa-cmp-meter"><span style={{ width: `${m.value}%` }} /></div>
              </div>
            ))}
            {SAMPLE_REPORT_PUBLISHED && (
              <Link to="/sample-report/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
                {SAMPLE_REPORT_LINK_TEXT}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: 0 }}>If you can only pick one</h2>
        <div className="uxa-compare-prose">
          <p className="uxa-body">
            Most teams are not buying three tools. If you are pre-launch or in
            the first year, start with a scored audit: you usually do not have
            enough traffic for behavioural data to mean anything, and not enough
            budget or time for a research study. What you need is a fast,
            structured read on where the problems are.
          </p>
          <p className="uxa-body">
            If you are established, with real traffic and a research budget, the
            calculus shifts — but an audit is still worth running first, because
            it tells you where to point the more expensive tools instead of
            spending a research budget rediscovering the obvious.
          </p>
          {/* THE SECTION'S ANCHOR, and it shipped as a fourth grey paragraph.
              The handoff lifts this one sentence onto a sand card with a
              cross-hatch: it is the argument the whole section is built
              around, and it was reading exactly like the three paragraphs
              beside it. */}
          <div className="uxa-cmp-callout">
            <p>
              The trap is buying the most impressive-sounding tool for a stage
              you are not at. A research platform is wasted on a product that
              has not launched, and a heatmap is wasted on a page nobody is
              visiting.
            </p>
          </div>
          <p className="uxa-body">
            {`They also work together, and that is the honest recommendation: audit `
              + `first for a map of where the problems are, watch the flows it flags, `
              + `then research the ones you still do not understand. `}
            <Link className="uxa-inline-link" to="/blog/uxauditpro-vs-hotjar-vs-usertesting/">
              The long version, with the full reasoning
            </Link>
            {' goes through each of those in depth.'}
          </p>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: 0 }}>
          Pick by stage, not by feature list
        </h2>
        <div className="uxa-cmp-stages">
          {STAGES.map((st, i) => (
            <article
              key={st.stage}
              className={`uxa-cmp-stage${i === 0 ? ' uxa-cmp-stage--float' : ''}`}
            >
              <div className="uxa-cmp-label">{st.stage}</div>
              <h3 className="uxa-h3" style={{ margin: 'var(--space-2) 0 0' }}>{st.title}</h3>
              <p className="uxa-dense" style={{ color: 'var(--ink-2)', margin: 'var(--space-3) 0 0' }}>
                {st.body}
              </p>
              <div className="uxa-cmp-stage__rule" />
              <p className="uxa-dense uxa-cmp-stage__verdict" style={{ margin: 0 }}>{st.verdict}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: 0 }}>
          Questions people ask before switching
        </h2>
        {/* ds/AccordionItem, not the handoff's div with an onClick.
            The handoff opens each row from a <div sc-camel-on-click>, which is
            unreachable by keyboard and announces nothing. AccordionItem is the
            site's existing FAQ control -- a real <button>, aria-expanded wired
            to aria-controls, and the panel kept MOUNTED and [hidden] so the
            answer text is in the prerendered HTML whether or not anyone
            clicks. That last part matters on a page a crawler reads. */}
        <div
          className="uxa-cmp-float"
          style={{ maxWidth: 820, margin: 'var(--space-8) auto 0', padding: '0 var(--space-5)' }}
        >
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              question={f.q}
              open={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              style={i === FAQS.length - 1 ? { borderBottom: 'none' } : undefined}
            >
              <p className="uxa-body" style={{ color: 'var(--ink-2)', margin: 0, maxWidth: 640 }}>
                {f.a}
              </p>
            </AccordionItem>
          ))}
        </div>
      </section>

      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div className="uxa-compare-closing">
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>
            Start with the diagnosis
          </h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 560 }}>
            {`You get one free audit every month (${PLAN_PRICES.FREE.quota}), and it needs `
              + `nothing but a URL. Every audit after that is $${PLAN_PRICES.STARTER.usd}, `
              + `once, with no subscription.`}
          </p>
          <div className="uxa-compare-closing__cta">
            <span className="uxa-compare-closing__knob">
              {user ? (
                <Link to="/audit/new" className="uxa-submit uxa-submit--auto" data-testid="compare-cta">
                  Run a free audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              ) : (
                <button type="button" onClick={login} className="uxa-submit uxa-submit--auto" data-testid="compare-cta">
                  Run a free audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </button>
              )}
            </span>
          </div>
          <p className="uxa-compare-links">
            <Link to="/pricing/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
              See pricing
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </p>
          {SAMPLE_REPORT_PUBLISHED && (
            <p className="uxa-compare-links">
              <Link to="/sample-report/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
                {SAMPLE_REPORT_LINK_TEXT}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
